import React, { useEffect, useRef, useState } from "react";
import { Volume2, Pause, Play, Square, Rewind } from "lucide-react";

/**
 * Text-to-Speech controls using the browser Speech Synthesis API.
 * - No backend cost; runs entirely in the browser
 * - Props:
 *   - getText: function returning the plain text to read (called on Play)
 *   - label: e.g. "Listen to this reading"
 */
export default function TTSControls({ getText, label = "Listen" }) {
  const [supported, setSupported] = useState(true);
  const [state, setState] = useState("idle"); // idle | playing | paused
  const [voices, setVoices] = useState([]);
  const [voiceName, setVoiceName] = useState("");
  const [rate, setRate] = useState(1);
  const utterRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined" || !("speechSynthesis" in window)) {
      setSupported(false);
      return;
    }
    function loadVoices() {
      const v = window.speechSynthesis.getVoices();
      setVoices(v);
      // Prefer a US English natural voice if available
      const preferred = v.find((x) => /en-US/i.test(x.lang) && /natural|neural|premium|Samantha|Enhanced/i.test(x.name)) ||
                        v.find((x) => /en-US/i.test(x.lang)) ||
                        v[0];
      if (preferred && !voiceName) setVoiceName(preferred.name);
    }
    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;
    return () => {
      try { window.speechSynthesis.cancel(); } catch {}
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => () => { try { window.speechSynthesis?.cancel(); } catch {} }, []);

  function play() {
    if (!supported) return;
    const s = window.speechSynthesis;
    // If paused, resume
    if (state === "paused") {
      s.resume();
      setState("playing");
      return;
    }
    // Fresh play
    s.cancel();
    const text = (getText?.() || "").trim();
    if (!text) return;
    const u = new SpeechSynthesisUtterance(text);
    const v = voices.find((x) => x.name === voiceName);
    if (v) u.voice = v;
    u.rate = rate;
    u.pitch = 1;
    u.onend = () => setState("idle");
    u.onerror = () => setState("idle");
    utterRef.current = u;
    s.speak(u);
    setState("playing");
  }

  function pause() {
    if (!supported) return;
    window.speechSynthesis.pause();
    setState("paused");
  }

  function stop() {
    if (!supported) return;
    window.speechSynthesis.cancel();
    setState("idle");
  }

  function restart() {
    stop();
    setTimeout(play, 100);
  }

  if (!supported) return null;

  return (
    <div className="dw-card p-3 md:p-4 flex flex-wrap items-center gap-2 md:gap-3" data-testid="tts-controls">
      <div className="flex items-center gap-1.5 shrink-0">
        <Volume2 className="w-4 h-4 text-[var(--dw-gold-soft)]" strokeWidth={1.5} />
        <span className="overline text-neutral-700">{label}</span>
      </div>

      <div className="flex items-center gap-1 ml-auto">
        {state !== "playing" ? (
          <button
            onClick={play}
            data-testid="tts-play"
            className="dw-btn-primary rounded-sm px-3 py-1.5 text-xs inline-flex items-center gap-1.5"
          >
            <Play className="w-3.5 h-3.5" strokeWidth={1.5} fill="currentColor" /> {state === "paused" ? "Resume" : "Play"}
          </button>
        ) : (
          <button
            onClick={pause}
            data-testid="tts-pause"
            className="dw-btn-outline rounded-sm px-3 py-1.5 text-xs inline-flex items-center gap-1.5"
          >
            <Pause className="w-3.5 h-3.5" strokeWidth={1.5} fill="currentColor" /> Pause
          </button>
        )}
        <button
          onClick={restart}
          disabled={state === "idle"}
          data-testid="tts-restart"
          className="dw-btn-outline rounded-sm px-2 py-1.5 text-xs inline-flex items-center gap-1 disabled:opacity-40"
          title="Restart"
        >
          <Rewind className="w-3.5 h-3.5" strokeWidth={1.5} />
        </button>
        <button
          onClick={stop}
          disabled={state === "idle"}
          data-testid="tts-stop"
          className="dw-btn-outline rounded-sm px-2 py-1.5 text-xs inline-flex items-center gap-1 disabled:opacity-40"
          title="Stop"
        >
          <Square className="w-3.5 h-3.5" strokeWidth={1.5} fill="currentColor" />
        </button>
      </div>

      {/* Rate + voice selectors */}
      <div className="flex items-center gap-2 basis-full sm:basis-auto text-xs text-neutral-600">
        <label className="flex items-center gap-1.5">
          <span className="uppercase tracking-widest text-[10px]">Speed</span>
          <select
            value={rate}
            onChange={(e) => setRate(parseFloat(e.target.value))}
            data-testid="tts-speed"
            className="border border-[var(--dw-navy)]/15 rounded-sm px-1.5 py-0.5 text-xs bg-white"
          >
            <option value="0.85">0.85×</option>
            <option value="1">1×</option>
            <option value="1.15">1.15×</option>
            <option value="1.3">1.3×</option>
            <option value="1.5">1.5×</option>
          </select>
        </label>
        {voices.length > 0 && (
          <label className="hidden md:flex items-center gap-1.5">
            <span className="uppercase tracking-widest text-[10px]">Voice</span>
            <select
              value={voiceName}
              onChange={(e) => setVoiceName(e.target.value)}
              data-testid="tts-voice"
              className="border border-[var(--dw-navy)]/15 rounded-sm px-1.5 py-0.5 text-xs bg-white max-w-[160px]"
            >
              {voices
                .filter((v) => /^en/i.test(v.lang))
                .map((v) => <option key={v.name} value={v.name}>{v.name}</option>)}
            </select>
          </label>
        )}
      </div>
    </div>
  );
}

// Strip markdown to plain-readable text for TTS
export function stripMarkdownForSpeech(md) {
  if (!md) return "";
  let t = md;
  t = t.replace(/```[\s\S]*?```/g, ""); // remove code blocks
  t = t.replace(/`([^`]+)`/g, "$1");
  t = t.replace(/\*\*(.+?)\*\*/g, "$1");
  t = t.replace(/\*(.+?)\*/g, "$1");
  t = t.replace(/^\s*\|.*\|\s*$/gm, ""); // remove table rows entirely (too noisy)
  t = t.replace(/^#{1,6}\s+/gm, "");
  t = t.replace(/^(?:- |\* |\d+\.\s+)/gm, "");
  t = t.replace(/\[([^\]]+)\]\([^)]+\)/g, "$1");
  t = t.replace(/&/g, "and");
  t = t.replace(/\n{2,}/g, ". ");
  t = t.replace(/\n/g, " ");
  t = t.replace(/\s{2,}/g, " ");
  return t.trim();
}
